import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { useAuth } from '../../../contexts/AuthContext';
import { wmsApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import { smartWarehouseService } from '../services/smartWarehouseService';
import { waveService } from '../services/waveService';
import receivingService from '../services/receivingService';
import countingService from '../services/countingService';
import { wmsPersonnelPermissionService } from '../services/wmsPersonnelPermissionService';
import { BRAND_GRADIENTS } from '../../../config/brand';
import WmsStationHeader from '../shared/WmsStationHeader';

type WmsScreenKind = 'picking' | 'packing' | 'receiving' | 'cyclecount';

interface ResolvedScreen {
  screenId: string;
  type: WmsScreenKind;
  name: string;
  warehouseId?: string;
  warehouseName?: string;
  route: string;
  workCount?: number | null;
}

const SCREEN_TYPES: WmsScreenKind[] = ['picking', 'packing', 'receiving', 'cyclecount'];

const SCREEN_TYPE_META: Record<
  WmsScreenKind,
  { label: string; routePrefix: string; color: string; Icon: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }> }
> = {
  picking: { label: 'Toplama', routePrefix: '/wms/toplama', color: '#1976d2', Icon: LocalShippingIcon },
  packing: { label: 'Paketleme', routePrefix: '/wms/paketleme', color: '#9c27b0', Icon: Inventory2Icon },
  receiving: { label: 'Mal Kabul', routePrefix: '/wms/malkabul', color: '#2e7d32', Icon: MoveToInboxIcon },
  cyclecount: { label: 'Stok Sayim', routePrefix: '/wms/stoksayim', color: '#ed6c02', Icon: FactCheckIcon },
};

const WmsScreenChooser: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userDetails } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [screens, setScreens] = useState<ResolvedScreen[]>([]);

  const displayName = useMemo(() => {
    if (userDetails?.firstName || userDetails?.lastName) {
      return `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim();
    }
    return currentUser?.email || 'Personel';
  }, [currentUser, userDetails]);

  useEffect(() => {
    let cancelled = false;

    const loadScreens = async () => {
      try {
        if (!currentUser) {
          navigate('/login', { replace: true });
          return;
        }

        if (userDetails?.role === 'admin') {
          navigate('/accounting/dashboard', { replace: true });
          return;
        }

        const companyId = userDetails?.companyId || (currentUser as any)?.companyId;
        if (!companyId) {
          setError('Şirket bilgisi bulunamadı. Lütfen tekrar giriş yapınız.');
          return;
        }

        const resolvedPersonnel = await wmsPersonnelPermissionService.resolvePersonnel(
          companyId,
          currentUser.email || ''
        );
        const personnelId = resolvedPersonnel?.personnelId || currentUser.uid;
        const personnelName =
          resolvedPersonnel?.personnelName ||
          `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim() ||
          currentUser.email ||
          '';

        // Faz 3: WMS ekran erisimleri SQL'den (servis HybridFallback yonetir).
        const activeScreenIds = await wmsPersonnelPermissionService.getMyWmsScreens(companyId, personnelId);

        if (activeScreenIds.length === 0) {
          setError('Yetkili olduğunuz WMS ekranı bulunamadı.');
          return;
        }

        let warehouseLookup: Record<string, string> = {};
        let warehouseLookupLoaded = false;
        try {
          const warehouses = await smartWarehouseService.getAllList();
          warehouseLookup = warehouses.reduce<Record<string, string>>((acc, warehouse) => {
            if (warehouse.id) acc[warehouse.id] = warehouse.warehouseName || 'Depo';
            return acc;
          }, {});
          warehouseLookupLoaded = true;
        } catch (warehouseErr) {
          // Depo listesi anlik bir hatadan dolayi alinamadiysa stale filtresini uygulamadan
          // tum ekranlari gosteriyoruz; aksi halde gecici bir API hatasinda tum personel
          // ekransiz kalir.
          console.warn('[WmsScreenChooser] Depo listesi alinamadi, stale filtresi atlaniyor:', warehouseErr);
          warehouseLookup = {};
          warehouseLookupLoaded = false;
        }

        const resolvedScreens: ResolvedScreen[] = [];
        let filteredStaleCount = 0;

        for (const screenId of activeScreenIds) {
          for (const type of SCREEN_TYPES) {
            try {
              const res = await wmsApi.get(ACCOUNTING_API.WMS_SCREEN_DETAIL(type, screenId));
              const data = res.data?.data || res.data;
              if (!data) continue;

              const screenName = data.name || data.Name || SCREEN_TYPE_META[type].label;
              const warehouseId = data.warehouseId || data.WarehouseId;
              // Deposu silinmis ekranlari secim listesinde gosterme.
              // Yalnizca depo listesi basarili sekilde alindiysa filtre uygulanir;
              // aksi halde anlik API hatasinda tum personeli ekransiz birakmamak icin atlanir.
              if (warehouseLookupLoaded && warehouseId && !warehouseLookup[warehouseId]) {
                filteredStaleCount += 1;
                break;
              }
              const warehouseName = data.warehouseName || data.WarehouseName || (warehouseId ? warehouseLookup[warehouseId] : undefined);

              resolvedScreens.push({
                screenId,
                type,
                name: screenName,
                warehouseId,
                warehouseName,
                route: `${SCREEN_TYPE_META[type].routePrefix}/${screenId}`,
              });
              break;
            } catch (err: any) {
              if (err?.response?.status && err.response.status !== 404) {
                console.warn(`[WmsScreenChooser] WMS ekrani cozumlenemedi (${type}/${screenId}):`, err);
              }
            }
          }
        }

        if (cancelled) return;

        if (filteredStaleCount > 0) {
          console.info(`[WmsScreenChooser] ${filteredStaleCount} adet deposu silinmis ekran filtrelendi.`);
        }

        if (resolvedScreens.length === 0) {
          setError('Yetkili ekranlarınız bulunamadı veya silinmiş olabilir.');
          return;
        }
        if (resolvedScreens.length === 1) {
          navigate(resolvedScreens[0].route, { replace: true });
          return;
        }

        const activeStatusSet = new Set(['created', 'inprogress', 'pending', 'active', 'open', 'started']);
        const screensWithWork = await Promise.all(
          resolvedScreens.map(async (screen) => {
            const warehouseId = screen.warehouseId;
            if (!warehouseId) return { ...screen, workCount: null };

            try {
              // Entegrasyon/detayli is tanimi gelince bu kurallar ekran bazli daha ince ayarlanabilir.
              if (screen.type === 'picking') {
                try {
                  const res = await waveService.getByPersonnel(warehouseId, personnelId);
                  const data = res.data?.data || res.data || [];
                  const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
                  const active = list.filter((w: any) => activeStatusSet.has(String(w?.status || '').toLowerCase()));
                  return { ...screen, workCount: active.length };
                } catch {
                  // Deploy/farkli ortam farklari icin by-warehouse fallback.
                  const res = await waveService.getByWarehouse(warehouseId, { page: 1, pageSize: 100 });
                  const data = res.data?.data || res.data || [];
                  const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
                  const mine = list.filter((w: any) => {
                    const statusOk = activeStatusSet.has(String(w?.status || '').toLowerCase());
                    const assignedId = String(w?.assignedPersonnelId || w?.assignedPickerId || '');
                    const assignedName = String(w?.assignedPickerName || '');
                    return statusOk && (
                      (assignedId && assignedId === personnelId) ||
                      (assignedName && personnelName && assignedName === personnelName)
                    );
                  });
                  return { ...screen, workCount: mine.length };
                }
              }

              if (screen.type === 'packing') {
                const res = await wmsApi.get(ACCOUNTING_API.WMS_PACKING_PENDING(warehouseId));
                const data = res.data?.data || res.data || [];
                const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
                return { ...screen, workCount: list.length };
              }

              if (screen.type === 'receiving') {
                const raw = await receivingService.getSessions(warehouseId, { page: 1, pageSize: 100 });
                const data = raw?.data || raw || [];
                const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
                const active = list.filter((s: any) => activeStatusSet.has(String(s?.status || '').toLowerCase()));
                return { ...screen, workCount: active.length };
              }

              if (screen.type === 'cyclecount') {
                try {
                  const list = await countingService.getMyTasksByPersonnel(warehouseId, personnelId);
                  const active = (Array.isArray(list) ? list : []).filter((t: any) =>
                    activeStatusSet.has(String(t?.status || '').toLowerCase())
                  );
                  return { ...screen, workCount: active.length };
                } catch {
                  const raw = await countingService.getTasks(warehouseId, { page: 1, pageSize: 100 });
                  const data = raw?.data || raw || [];
                  const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
                  const mine = list.filter((t: any) => {
                    const statusOk = activeStatusSet.has(String(t?.status || '').toLowerCase());
                    const assignedId = String(t?.assignedPersonnelId || '');
                    const assignedName = String(t?.assignedPersonnelName || t?.assignedCashierName || '');
                    return statusOk && (
                      (assignedId && assignedId === personnelId) ||
                      (assignedName && personnelName && assignedName === personnelName)
                    );
                  });
                  return { ...screen, workCount: mine.length };
                }
              }
            } catch {
              return { ...screen, workCount: 0 };
            }

            return { ...screen, workCount: 0 };
          })
        );

        setScreens(screensWithWork);
      } catch (err) {
        if (!cancelled) {
          console.error('[WmsScreenChooser] Yukleme hatasi:', err);
          setError('Ekranlar yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadScreens();

    return () => {
      cancelled = true;
    };
  }, [currentUser, userDetails, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: BRAND_GRADIENTS?.vertical || 'linear-gradient(180deg, #f5f7fb 0%, #e8edf5 100%)',
        py: { xs: 4, md: 8 },
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 960, mx: 'auto' }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <WmsStationHeader
            title={`Merhaba, ${displayName}`}
            subtitle="Çalışmak istediğiniz WMS ekranını seçin."
            badgeLabel={`${screens.length || 0} ekran yetkisi`}
            badgeColor="primary"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            İş akışını başlatmak için aşağıdaki kartlardan birine dokunun.
          </Typography>
        </Paper>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={() => navigate('/personnel/tasks')}>
              Görev Panosuna Git
            </Button>
          </>
        )}

        {!loading && !error && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
              Yetkili olduğunuz ekranlar:
            </Typography>
            <Grid container spacing={2}>
              {screens.map((screen) => {
                const meta = SCREEN_TYPE_META[screen.type];
                const Icon = meta.Icon;
                return (
                  <Grid item xs={12} md={6} key={`${screen.type}-${screen.screenId}`}>
                    <Card
                      elevation={2}
                      sx={{
                        borderRadius: 3,
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 6,
                        },
                      }}
                    >
                      <CardActionArea onClick={() => navigate(screen.route)}>
                        <CardContent>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: meta.color, width: 56, height: 56 }}>
                              <Icon fontSize="large" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Chip
                                size="small"
                                label={meta.label}
                                sx={{ bgcolor: `${meta.color}15`, color: meta.color, fontWeight: 600, mb: 0.5 }}
                              />
                              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                                {screen.name}
                              </Typography>
                              {screen.warehouseName && (
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {screen.warehouseName}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'inline-block',
                                  mt: 0.25,
                                  color:
                                    screen.workCount == null
                                      ? 'text.secondary'
                                      : screen.workCount > 0
                                        ? 'success.main'
                                        : 'text.secondary',
                                  fontWeight: 600,
                                }}
                              >
                                {screen.workCount == null
                                  ? 'İş durumu alınamadı'
                                  : screen.workCount > 0
                                    ? `İş var (${screen.workCount})`
                                    : 'İş yok'}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default WmsScreenChooser;
